import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Rating from '@mui/material/Rating';
import Typography from '@mui/material/Typography';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/shared/components/ui';
import { travelerReviewsApi } from '@/features/booking/api/reviews.api';
import type { Booking } from '@/types';

interface BookingReviewDialogProps {
  booking: Booking | null;
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
}

export function BookingReviewDialog({ booking, open, onClose, onSubmitted }: BookingReviewDialogProps) {
  const [rating, setRating] = useState<number | null>(5);
  const [comment, setComment] = useState('');

  const submit = useMutation({
    mutationFn: () =>
      travelerReviewsApi.create({
        bookingId: booking!.id,
        rating: rating!,
        comment: comment.trim(),
      }),
    onSuccess: () => {
      setComment('');
      setRating(5);
      onSubmitted();
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Leave a review</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Share your experience with {booking?.guide?.fullName ?? 'your guide'}.
        </Typography>
        <Rating
          value={rating}
          onChange={(_, v) => setRating(v)}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Your review"
          multiline
          minRows={4}
          fullWidth
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          helperText="Minimum 10 characters"
        />
      </DialogContent>
      <DialogActions>
        <Button intent="secondary-outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          intent="primary-dark"
          disabled={!rating || comment.trim().length < 10 || submit.isPending}
          onClick={() => submit.mutate()}
        >
          Submit review
        </Button>
      </DialogActions>
    </Dialog>
  );
}
